import React, { useEffect, useState, lazy, Suspense } from "react";
import { Link, useLocation } from "react-router-dom";
import { AppBar, Toolbar, Typography, Container, Box, IconButton } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";

// Lazy load components that are below the fold
const MarketingPopup = lazy(() => import("./marketing/MarketingPopup"));
const Footer = lazy(() => import("./Footer"));

const ADMIN_SECRET_PATH = "/movie-admin-2024";

// Memoized styles to prevent recalculation
const styles = {
  navLink: {
    color: "inherit",
    textDecoration: "none",
    padding: "6px 16px",
    fontSize: "0.875rem",
    fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
    fontWeight: 500,
    lineHeight: 1.75,
    borderRadius: "4px",
    textTransform: "none",
    minWidth: "64px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
    cursor: "pointer"
  },
  mobileNavLink: {
    color: "inherit",
    textDecoration: "none",
    padding: "6px 16px",
    fontSize: "0.875rem",
    fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
    fontWeight: 500,
    lineHeight: 1.75,
    borderRadius: "4px",
    textTransform: "none",
    width: "100%",
    textAlign: "center",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
    cursor: "pointer"
  }
};

// Memoized components
const NavLink = React.memo(({ to, children, onClick }) => (
  <Link
    to={to}
    className="nav-link"
    onClick={onClick}
    style={styles.navLink}
    onMouseEnter={(e) => e.target.style.backgroundColor = "rgba(255, 255, 255, 0.08)"}
    onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
  >
    {children}
  </Link>
));

const MobileNavLink = React.memo(({ to, children, onClick }) => (
  <Link
    to={to}
    className="nav-link"
    onClick={onClick}
    style={styles.mobileNavLink}
    onMouseEnter={(e) => e.target.style.backgroundColor = "rgba(255, 255, 255, 0.08)"}
    onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
  >
    {children}
  </Link>
));

const Logo = React.memo(() => (
  <Link
    to="/"
    style={{
      textDecoration: "none",
      color: "inherit",
      display: "flex",
      alignItems: "center",
      height: "80px",
      padding: "0 32px",
      transition: "all 0.3s ease",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "-4px",
      }}
    >
       <Box
       component="img"
       src="/movie.svg"
        alt="MovieHub Logo"
        loading="eager"
        sx={{
          height: 90,
          width: 90,
          objectFit: "contain",
          filter: "drop-shadow(0 0 8px rgba(255,255,255,0.2))",
          display: "block",
        }}
      />
      <Typography
        variant="h5"
        component="div"
        sx={{
          fontWeight: 700,
          letterSpacing: "0.5px",
          color: "#fff",
          textShadow: "0 2px 10px rgba(0,0,0,0.3)",
          fontFamily: '"Poppins", sans-serif',
          fontSize: { xs: "1.2rem", sm: "1.7rem" },
        }}
      >
        MovieHub
      </Typography>
    </Box>
  </Link>
));

const MobileMenu = React.memo(({ isOpen, onClose }) => (
  isOpen && (
    <Box
      sx={{
        display: { xs: "flex", md: "none" },
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        py: 2,
        bgcolor: "rgba(18, 18, 18, 0.9)",
      }}
    >
      <MobileNavLink to="/" onClick={onClose}>Home</MobileNavLink>
      <MobileNavLink to="/about" onClick={onClose}>About</MobileNavLink>
      <MobileNavLink to="/request-movie" onClick={onClose}>Request Movie</MobileNavLink>
    </Box>
  )
));

const DesktopNav = React.memo(() => (
  <Box sx={{ display: { xs: "none", md: "flex" }, gap: 2 }}>
    <NavLink to="/">Home</NavLink>
    <NavLink to="/about">About</NavLink>
    <NavLink to="/request-movie">Request Movie</NavLink>
  </Box>
));

const Layout = ({ children }) => {
  const location = useLocation();
  const [showPopup, setShowPopup] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Reset popup state on route change
  useEffect(() => {
    setShowPopup(true);
  }, [location.pathname]);

  // Check if current path is admin-related
  const isAdminRoute = location.pathname === ADMIN_SECRET_PATH || location.pathname.includes("/admin");

  // For admin routes, render only the children without the layout
  if (isAdminRoute) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.default",
        }}
      >
        {children}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#000000",
        background: `
          radial-gradient(circle at center, rgba(30,30,30,0.95) 0%, rgba(0,0,0,0.95) 100%),
          repeating-radial-gradient(circle at center, #000 0, #000 1px, transparent 1px, transparent 100%),
          #000
        `,
        backgroundSize: "100% 100%, 3px 3px, 100% 100%",
        backgroundBlendMode: "overlay",
      }}
    >
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: "rgba(18, 18, 18, 0.7)",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
        }}
      >
        <Toolbar
          sx={{
            height: 80,
            background: "rgba(255, 255, 255, 0.05)",
            backdropFilter: "blur(8px)",
            boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)",
            border: "1px solid rgba(255,255,255,0.1)",
            p: 0,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Logo />
          <DesktopNav />
          <IconButton
            sx={{ display: { xs: "flex", md: "none" }, color: "white" }}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
        <MobileMenu isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />
      </AppBar>

      <Box sx={{ height: "80px" }} />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mt: -4,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Suspense>
          {showPopup && <MarketingPopup onClose={() => setShowPopup(false)} />}
        </Suspense>
        {children}
      </Box>

      <Suspense>
        <Footer />
      </Suspense>
    </Box>
  );
};

export default React.memo(Layout);
